import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/Toast';
import { formatApiError } from '../../utils/errors';
import { analyticsApi, type AttentionInboxItemDto, type AttentionInboxDto } from '../../api/analytics';
import { workspacesApi, setWorkspaceId, type WorkspaceInfo } from '../../api/workspaces';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { CATEGORY_BADGE, SEVERITY_DOT, companyColor, CATEGORY_ORDER } from '../../components/analytics/inboxShared';
import { formatMoney } from '../../utils/formatMoney';

/**
 * Darbai — the PORTFOLIO work queue: all companies' attention items in one
 * ranked list. The strategy's #1 differentiator: "Visos klientų įmonės.
 * Viena darbų eilė." The accountant should not enter each company to discover
 * it needs attention.
 *
 * Client-side aggregation: one parallel inbox call per workspace (the per-
 * workspace react-query key already exists). With 3-15 workspaces this is
 * instant; past ~20, migrate to a backend portfolio endpoint.
 */

interface PortfolioItem extends AttentionInboxItemDto {
  workspaceId: string;
  workspaceName: string;
}

export default function PortfolioPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enableAnalytics, loading: flagsLoading } = useFeatureFlags();

  const { data: workspaces, isLoading: wsLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });

  // One parallel inbox fetch per workspace
  const inboxQueries = useQuery({
    queryKey: ['portfolio-inbox', (workspaces ?? []).map(w => w.id).sort().join(',')],
    queryFn: async () => {
      if (!workspaces?.length) return {};
      const entries = await Promise.all(
        workspaces.map(async ws => {
          try {
            const inbox = await fetchInboxFor(ws.id);
            return [ws.id, inbox] as const;
          } catch {
            return [ws.id, null] as const;
          }
        }),
      );
      return Object.fromEntries(entries) as Record<string, AttentionInboxDto | null>;
    },
    enabled: !!workspaces?.length && !flagsLoading && enableAnalytics,
    staleTime: 60_000,
  });

  const items = useMemo(() => {
    const all: PortfolioItem[] = [];
    for (const ws of workspaces ?? []) {
      const inbox = inboxQueries.data?.[ws.id];
      if (!inbox?.items) continue;
      for (const item of inbox.items) {
        // The portfolio queue is the Darbai view — the system's completed work
        // (Atlikta automatiškai) lives inside each company's Dėmesys tabs.
        if (item.automated) continue;
        all.push({ ...item, workspaceId: ws.id, workspaceName: ws.companyName || ws.name });
      }
    }
    // Same rank as the per-company inbox: category → dueDate → amount → severity → time
    return all.sort((a, b) => {
      const cat = (CATEGORY_ORDER[a.category] ?? 9) - (CATEGORY_ORDER[b.category] ?? 9);
      if (cat !== 0) return cat;
      if (a.category === 'Deadline' && b.category === 'Deadline') {
        const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        if (ad !== bd) return ad - bd;
      }
      if ((b.amount ?? 0) !== (a.amount ?? 0)) return (b.amount ?? 0) - (a.amount ?? 0);
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [workspaces, inboxQueries.data]);

  const companySummaries = useMemo(() => {
    return (workspaces ?? []).map(ws => {
      const inbox = inboxQueries.data?.[ws.id];
      // Readiness = open work only (automated completions are not work).
      const count = (inbox?.items ?? []).filter(i => !i.automated).length;
      return { ws, count, generated: inbox?.generatedAtUtc };
    }).sort((a, b) => b.count - a.count);
  }, [workspaces, inboxQueries.data]);

  const enterWorkspace = async (ws: WorkspaceInfo, path?: string) => {
    try {
      await workspacesApi.activate(ws.id);
      setWorkspaceId(ws.id);
      queryClient.invalidateQueries();
      navigate(path ?? '/analytics');
    } catch (err) {
      // Stay put — but SAY so: a silent no-op on a company switch reads as "broken".
      addToast(t('layout.workspaceSwitchFailed') + ' — ' + formatApiError(err), 'error');
    }
  };

  const drillItem = (item: PortfolioItem) => {
    const base = '/analytics';
    switch (item.drillKind) {
      case 'Invoice':
        if (item.invoiceId) return enterWorkspace(
          { id: item.workspaceId } as WorkspaceInfo, `${base}?view=invoice&invoice=${item.invoiceId}`);
        break;
      case 'Journal':
        if (item.journalEntryId) return enterWorkspace(
          { id: item.workspaceId } as WorkspaceInfo, `${base}?view=journal&entry=${item.journalEntryId}`);
        break;
      case 'Customer':
        if (item.personId) return enterWorkspace(
          { id: item.workspaceId } as WorkspaceInfo, `${base}?view=customer&person=${item.personId}`);
        break;
    }
    return enterWorkspace({ id: item.workspaceId } as WorkspaceInfo, base);
  };

  if (!flagsLoading && !enableAnalytics) {
    return null;
  }

  const loading = wsLoading || (inboxQueries.isLoading && !!workspaces?.length);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100 mb-4">
        <h1 className="text-xl font-semibold text-gray-900">{t('darbai.title')}</h1>
        <span className="text-xs text-gray-400">
          {t('darbai.summary', { companies: workspaces?.length ?? 0, items: items.length })}
        </span>
        <div className="flex-1" />
      </div>

      {loading && <div className="py-16 text-center text-gray-400">{t('analytics.loading')}</div>}

      {!loading && items.length === 0 && (
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 flex items-center justify-center w-12 h-12 rounded-2xl bg-green-50 border border-green-100">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-base font-medium text-gray-800">{t('darbai.allClear')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('darbai.allClearHint')}</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-6">
          {/* Company readiness summary */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {companySummaries.map(({ ws, count }) => (
              <button
                key={ws.id}
                onClick={() => enterWorkspace(ws)}
                className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-primary-300 hover:shadow-sm transition-colors"
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${companyColor(ws.id)}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{ws.companyName || ws.name}</div>
                  <div className="text-xs text-gray-400">
                    {count === 0 ? t('darbai.companyClear') : t('darbai.companyAttention', { count })}
                  </div>
                </div>
                {count > 0 && (
                  <span className="min-w-[22px] h-[22px] px-1 inline-flex items-center justify-center text-xs font-bold rounded-full bg-primary-100 text-primary-800">
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* The merged ranked list */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('darbai.queueTitle')}</h2>
            <div className="space-y-1">
              {items.map((item, i) => {
                const overdue = item.daysOverdue !== null && item.daysOverdue > 0;
                return (
                  <button
                    key={`${item.workspaceId}-${i}`}
                    onClick={() => drillItem(item)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50/50 transition-colors group"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[item.severity ?? ''] ?? 'bg-gray-300'}`} />
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${CATEGORY_BADGE[item.category] ?? 'bg-gray-100 text-gray-600'}`}>
                      {t(`analytics.inbox.cat.${item.category}`)}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${companyColor(item.workspaceId)}`} title={item.workspaceName} />
                    <span className="text-xs text-gray-400 flex-shrink-0 max-w-[140px] truncate">{item.workspaceName}</span>
                    <span className="flex-1 text-sm text-gray-800 truncate">{item.title}</span>
                    {item.amount != null && (
                      <span className={`text-sm font-medium tabular-nums ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
                        {formatMoney(item.amount)}
                      </span>
                    )}
                    {item.dueDate && (
                      <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
                        {item.daysOverdue != null && item.daysOverdue > 0
                          ? t('analytics.inbox.overdueBy', { days: item.daysOverdue })
                          : new Date(item.dueDate).toLocaleDateString('lt-LT')}
                      </span>
                    )}
                    <svg className="w-4 h-4 text-gray-300 group-hover:text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Fetch a workspace's inbox with its own header (bypasses the page-level active workspace). */
async function fetchInboxFor(workspaceId: string): Promise<AttentionInboxDto> {
  const { getWorkspaceId } = await import('../../api/workspaces');
  const saved = getWorkspaceId(); // restore after
  setWorkspaceId(workspaceId);
  try {
    return await analyticsApi.inbox();
  } finally {
    if (saved) setWorkspaceId(saved);
  }
}
